import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { useMyScheduleJobs } from '../hooks/useMyScheduleJobs';
import { useMyAssignedWorkOrders } from '../hooks/useMyAssignedWorkOrders';
import { useSchedulePermissions } from '../hooks/useSchedulePermissions';
import { useJobDetailsPanel } from '../hooks/useJobDetailsPanel';
import EditJobModal from '../components/EditJobModal';
import JobDetailsSlidePanel from '../components/job-details/JobDetailsSlidePanel';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';
import { useAuth } from '../contexts/AuthContext';
import MyJobsColumn from '../components/my-schedule/layout/MyJobsColumn';
import MyWorkOrdersColumn from '../components/my-schedule/layout/MyWorkOrdersColumn';
import MyScheduleTabs from '../components/my-schedule/layout/MyScheduleTabs';
import MyScheduleHeader from '../components/my-schedule/layout/MyScheduleHeader';
import { Link } from 'react-router-dom';
import { BULK_COMPLETE_JOBS } from '../graphql/mutations';
import { toast } from 'react-hot-toast';

const ACTIVE_TAB_STORAGE_KEY = 'my-schedule-active-tab';
const VALID_TABS = ['jobs', 'workOrders'];

export default function MySchedule() {
  const { user } = useAuth() || {};
  const client = useApolloClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  
  // Column layout state - default to 1 column
  const [columnLayout, setColumnLayout] = useState(1);
  
  // Active tab state - 'jobs' (default) | 'workOrders'
  const [activeTab, setActiveTab] = useState('jobs');
  
  // Selection mode state for bulk completion
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [isBulkCompleting, setIsBulkCompleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Work order detail slide panel state
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [isWorkOrderPanelOpen, setIsWorkOrderPanelOpen] = useState(false);
  
  // Job details slide panel state
  const {
    selectedJobId,
    isPanelOpen,
    openJobDetails,
    closeJobDetails,
    handleBrowserNavigation
  } = useJobDetailsPanel();
  
  // Load column layout preference from localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('my-schedule-column-layout');
    if (savedLayout && (savedLayout === '1' || savedLayout === '2')) {
      setColumnLayout(parseInt(savedLayout));
    }
  }, []);
  
  // Load active tab preference from localStorage
  useEffect(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    if (savedTab && VALID_TABS.includes(savedTab)) {
      setActiveTab(savedTab);
    }
  }, []);
  
  // Save column layout preference to localStorage
  const handleColumnLayoutChange = (layout) => {
    setColumnLayout(layout);
    localStorage.setItem('my-schedule-column-layout', layout.toString());
  };
  
  // Save active tab preference to localStorage
  const handleTabChange = useCallback((nextTab) => {
    if (!VALID_TABS.includes(nextTab)) return;
    setActiveTab(nextTab);
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, nextTab);
    if (nextTab === 'workOrders') {
      setIsSelectionMode(false);
      setSelectedJobIds(new Set());
    }
  }, []);
  
  const handleViewWorkOrder = useCallback((workOrderId) => {
    setSelectedWorkOrderId(workOrderId);
    setIsWorkOrderPanelOpen(true);
  }, []);
  
  const handleCloseWorkOrderPanel = useCallback(() => {
    setIsWorkOrderPanelOpen(false);
    setSelectedWorkOrderId(null);
  }, []);
  
  // Get jobs assigned to the current user
  const {
    myPendingJobs,
    jobsLoading,
    isLoadingMore,
    hasMore,
    handleLoadMore,
    refetchAllJobs,
    forceRefresh,
    totalLoadedJobs
  } = useMyScheduleJobs(user, searchTerm, page);
  
  // Get work orders directly assigned to the current user (OPEN + IN_PROGRESS)
  const {
    openOrders,
    inProgressOrders,
    total: workOrdersTotal,
    loading: workOrdersLoading,
    error: workOrdersError,
    refetch: refetchWorkOrders,
    hasMore: hasMoreWorkOrders,
    isLoadingMore: isLoadingMoreWorkOrders,
    loadMore: loadMoreWorkOrders
  } = useMyAssignedWorkOrders(user, searchTerm);
  
  // Get all jobs as a flat array for counting
  const allJobs = useMemo(() => {
    return Object.values(myPendingJobs).flat();
  }, [myPendingJobs]);
  
  const totalAssignedWorkOrders = openOrders.length + inProgressOrders.length;
  
  // 🔒 SECURITY: Handle PWA cache issues by refreshing when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.id) {
        forceRefresh();
        refetchWorkOrders();
      }
    };

    const handleFocus = () => {
      if (user?.id) {
        forceRefresh();
        refetchWorkOrders();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id, forceRefresh, refetchWorkOrders]);
  
  // Handle browser navigation for job details panel
  useEffect(() => {
    window.addEventListener('popstate', handleBrowserNavigation);
    return () => {
      window.removeEventListener('popstate', handleBrowserNavigation);
    };
  }, [handleBrowserNavigation]);
  
  // Reuse permissions hook for job editing
  const {
    onJobEdit,
    canEditJobs,
    editingJob,
    closeEditModal
  } = useSchedulePermissions();
  
  // Bulk complete mutation
  const [bulkCompleteJobsMutation] = useMutation(BULK_COMPLETE_JOBS, {
    onCompleted: () => {
      // Clear Apollo cache for job-related queries
      try {
        client.cache.evict({ fieldName: 'jobs' });
        client.cache.evict({ fieldName: 'jobsForAssignments' });
        client.cache.evict({ fieldName: 'myScheduleJobs' });
        client.cache.evict({ fieldName: 'readyForInspectionJobs' });
        client.cache.evict({ fieldName: 'completedJobs' });
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
        client.cache.gc();
      } catch (error) {
        console.error('Error clearing cache after bulk complete:', error);
      }
    }
  });
  
  // Handle job updates via modal
  const handleJobUpdate = (forceNetworkRefresh = false) => {
    // 🎯 ENHANCED: Force network refresh for reservation updates to ensure computed fields recalculate
    // Force a complete refresh of all job data to ensure proper categorization
    refetchAllJobs(forceNetworkRefresh);
  };
  
  // Handle load more pagination
  const handleLoadMoreJobs = () => {
    handleLoadMore();
  };
  
  // Toggle selection mode
  const handleToggleSelectionMode = () => {
    setIsSelectionMode(prev => !prev);
    setSelectedJobIds(new Set()); // Clear selections when toggling
  };
  
  // Handle individual job selection
  const handleJobSelect = (jobId, isSelected) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(jobId);
      } else {
        next.delete(jobId);
      }
      return next;
    });
  };
  
  // Get selected jobs details for the confirmation dialog
  const selectedJobsDetails = useMemo(() => {
    const selectedJobs = allJobs.filter(job => selectedJobIds.has(job.id));
    const inspectionJobs = selectedJobs.filter(j => j.category === 'INSPECTION');
    const cleaningJobs = selectedJobs.filter(j => j.category === 'CLEANING');
    const otherJobs = selectedJobs.filter(j => !['INSPECTION', 'CLEANING'].includes(j.category));
    
    return {
      total: selectedJobs.length,
      inspection: inspectionJobs.length,
      cleaning: cleaningJobs.length,
      other: otherJobs.length,
      jobs: selectedJobs
    };
  }, [allJobs, selectedJobIds]);
  
  // Show confirmation dialog before bulk complete
  const handleBulkCompleteClick = () => {
    if (selectedJobIds.size === 0) return;
    setShowConfirmDialog(true);
  };
  
  // Execute bulk complete after confirmation
  const handleConfirmBulkComplete = async () => {
    if (selectedJobIds.size === 0) return;
    
    setShowConfirmDialog(false);
    setIsBulkCompleting(true);
    
    try {
      const result = await bulkCompleteJobsMutation({
        variables: {
          input: {
            jobIds: Array.from(selectedJobIds)
          }
        }
      });
      
      const data = result.data?.bulkCompleteJobs;
      
      if (data) {
        if (data.successCount > 0) {
          toast.success(`Successfully completed ${data.successCount} job${data.successCount > 1 ? 's' : ''}`);
        }
        
        if (data.failureCount > 0) {
          // Show details about failures
          const failedJobs = data.results.filter(r => !r.success);
          const failureMessages = failedJobs.map(f => f.error).join(', ');
          toast.error(`${data.failureCount} job${data.failureCount > 1 ? 's' : ''} failed: ${failureMessages}`);
        }
      }
      
      // Exit selection mode and refresh data
      setIsSelectionMode(false);
      setSelectedJobIds(new Set());
      forceRefresh();
      
    } catch (error) {
      console.error('Error during bulk complete:', error);
      toast.error(`Failed to complete jobs: ${error.message}`);
    } finally {
      setIsBulkCompleting(false);
    }
  };
  
  // Cancel confirmation dialog
  const handleCancelConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const isJobsTab = activeTab === 'jobs';
  const isWorkOrdersTab = activeTab === 'workOrders';

  // Create the column toggle buttons to pass as leftContent (Jobs tab only)
  const columnToggleButtons = isJobsTab ? (
    <div className="hidden md:flex bg-gray-100 rounded-lg p-1">
      <button
        onClick={() => handleColumnLayoutChange(1)}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          columnLayout === 1
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        1 Column
      </button>
      <button
        onClick={() => handleColumnLayoutChange(2)}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          columnLayout === 2
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
        2 Column
      </button>
    </div>
  ) : null;

  // Right-side header content - changes based on active tab
  const headerRightContent = isJobsTab ? (
    <Link
      to="/dashboard/completed-jobs"
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
    >
      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      View Completed Jobs
    </Link>
  ) : (
    <Link
      to="/dashboard/work-orders"
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
    >
      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
      </svg>
      View All Work Orders
    </Link>
  );
  
  return (
    <div className="mx-auto pb-6 lg:px-8">
      {/* Header with user info */}
      <div className="mt-1 lg:mt-0 mb-2 lg:mb-6 text-center md:text-left">
        <h1 className="text-2xl font-bold text-white lg:text-gray-900">My Jobs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Current Schedule for <strong>{user?.firstName || 'User'}{user?.lastName ? ` ${user.lastName}` : ''}</strong>
        </p>
      </div>
      
      {/* Tabs */}
      <div className="mb-4">
        <MyScheduleTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          jobsCount={allJobs.length}
          workOrdersCount={totalAssignedWorkOrders}
          isLoading={(isJobsTab && jobsLoading) || (isWorkOrdersTab && workOrdersLoading)}
        />
      </div>
      
      {/* Search header (selection-mode controls only apply on Jobs tab) */}
      <div className="hidden md:block">
        <MyScheduleHeader
          title={isJobsTab ? 'Assigned Jobs' : 'Assigned Work Orders'}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          leftContent={columnToggleButtons}
          rightContent={headerRightContent}
          isSelectionMode={isJobsTab && isSelectionMode}
          onToggleSelectionMode={handleToggleSelectionMode}
          selectedCount={selectedJobIds.size}
          onBulkComplete={handleBulkCompleteClick}
          isBulkCompleting={isBulkCompleting}
          showSelectionToggle={isJobsTab}
        />
      </div>
      
      {/* Mobile selection mode header (Jobs tab only) */}
      {isJobsTab && isSelectionMode && (
        <div className="md:hidden mb-4 bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">
                {selectedJobIds.size} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {selectedJobIds.size > 0 && (
                <button
                  onClick={handleBulkCompleteClick}
                  disabled={isBulkCompleting}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {isBulkCompleting ? 'Completing...' : 'Complete'}
                </button>
              )}
              <button
                onClick={handleToggleSelectionMode}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile selection mode toggle (Jobs tab only, when not in selection mode) */}
      {isJobsTab && !isSelectionMode && (
        <div className="md:hidden mb-4">
          <button
            onClick={handleToggleSelectionMode}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Select Multiple Jobs
          </button>
        </div>
      )}
      
      {/* Tab content */}
      {isWorkOrdersTab ? (
        <div className="mt-4 lg:mt-6">
          <MyWorkOrdersColumn
            openOrders={openOrders}
            inProgressOrders={inProgressOrders}
            total={workOrdersTotal}
            loading={workOrdersLoading}
            error={workOrdersError}
            searchTerm={searchTerm}
            onViewWorkOrder={handleViewWorkOrder}
            hasMore={hasMoreWorkOrders}
            isLoadingMore={isLoadingMoreWorkOrders}
            onLoadMore={loadMoreWorkOrders}
          />
        </div>
      ) : columnLayout === 1 ? (
        /* Single Column Layout - Stack all components vertically */
        <div className="space-y-8 mt-4 lg:mt-6">
          <MyJobsColumn
            myPendingJobs={myPendingJobs}
            jobsLoading={jobsLoading}
            isLoadingMore={isLoadingMore}
            onJobEdit={onJobEdit}
            onJobViewDetails={openJobDetails}
            canEditJobs={canEditJobs}
            hasMore={hasMore}
            handleLoadMore={handleLoadMoreJobs}
            searchTerm={searchTerm}
            totalLoadedJobs={totalLoadedJobs}
            isSelectionMode={isSelectionMode}
            selectedJobIds={selectedJobIds}
            onJobSelect={handleJobSelect}
          />
        </div>
      ) : (
        /* Two Column Layout - Original layout */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
          {/* My Jobs Column */}
          <MyJobsColumn
            myPendingJobs={myPendingJobs}
            jobsLoading={jobsLoading}
            isLoadingMore={isLoadingMore}
            onJobEdit={onJobEdit}
            onJobViewDetails={openJobDetails}
            canEditJobs={canEditJobs}
            hasMore={hasMore}
            handleLoadMore={handleLoadMoreJobs}
            searchTerm={searchTerm}
            totalLoadedJobs={totalLoadedJobs}
            isSelectionMode={isSelectionMode}
            selectedJobIds={selectedJobIds}
            onJobSelect={handleJobSelect}
          />
        </div>
      )}

      {/* Edit Job Modal - reuse existing */}
      {editingJob && (
        <EditJobModal 
          job={editingJob} 
          onClose={closeEditModal} 
          onUpdate={handleJobUpdate} 
        />
      )}

      {/* Job Details Slide Panel */}
      <JobDetailsSlidePanel
        isOpen={isPanelOpen}
        onClose={closeJobDetails}
        jobId={selectedJobId}
        isPublicView={false}
        onJobStatusChange={handleJobUpdate}
      />
      
      {/* Work Order Detail Slide Panel */}
      <WorkOrderDetailSlidePanel
        isOpen={isWorkOrderPanelOpen}
        onClose={handleCloseWorkOrderPanel}
        workOrderId={selectedWorkOrderId}
        onDataChange={refetchWorkOrders}
      />
      
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleCancelConfirmDialog}
            ></div>

            {/* Center modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Confirm Bulk Completion
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-4">
                      You are about to complete <strong>{selectedJobsDetails.total}</strong> job{selectedJobsDetails.total > 1 ? 's' : ''}:
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
                      {selectedJobsDetails.inspection > 0 && (
                        <div className="flex items-center text-sm">
                          <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                          <span className="text-gray-700">
                            <strong>{selectedJobsDetails.inspection}</strong> Inspection job{selectedJobsDetails.inspection > 1 ? 's' : ''} 
                            <span className="text-gray-500 ml-1">(checklists will be auto-marked complete)</span>
                          </span>
                        </div>
                      )}
                      {selectedJobsDetails.cleaning > 0 && (
                        <div className="flex items-center text-sm">
                          <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                          <span className="text-gray-700">
                            <strong>{selectedJobsDetails.cleaning}</strong> Cleaning job{selectedJobsDetails.cleaning > 1 ? 's' : ''} 
                            <span className="text-gray-500 ml-1">(will be marked Ready for Inspection)</span>
                          </span>
                        </div>
                      )}
                      {selectedJobsDetails.other > 0 && (
                        <div className="flex items-center text-sm">
                          <span className="w-3 h-3 rounded-full bg-gray-500 mr-2"></span>
                          <span className="text-gray-700">
                            <strong>{selectedJobsDetails.other}</strong> Other job{selectedJobsDetails.other > 1 ? 's' : ''} 
                            <span className="text-gray-500 ml-1">(will be marked Completed)</span>
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-500 mt-4">
                      This action cannot be undone. Are you sure you want to continue?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleConfirmBulkComplete}
                  disabled={isBulkCompleting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBulkCompleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Completing...
                    </>
                  ) : (
                    'Complete Jobs'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancelConfirmDialog}
                  disabled={isBulkCompleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
